import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, SvgIcon, Tooltip } from '@mui/material';
import { Search } from '@mui/icons-material';
import Route from '@mui/icons-material/Route';
import LocalShipping from '@mui/icons-material/LocalShipping';
import EventBusy from '@mui/icons-material/EventBusy';
import ReportProblem from '@mui/icons-material/ReportProblem';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client, MarketOrderType, MarketOrderTypeFilterOptions } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { formatCutoffTimeForDisplay } from '../../../../utils/timeUtils';
import { isOrderTypeDSD } from '../../../../enums/OrderType';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

type Props = {
  onCreateNew: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilterChange?: (instance: string, market: string, searchTerm?: string) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  children: (parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<MarketOrderType>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const OrderChannelsListPageContent = ({ onCreateNew, onFilterChange, initialInstance, initialMarket, initialSearchTerm, children }: Props) => {
  const { t } = useTranslation('orderTypes');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['OrderTypeUpdate']);

  const [list, setList] = useState<MarketOrderType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const { instances, getMarkets, ensureMarketsLoaded, isMarketsLoading, defaultFiltersLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const pageSize = 20;

  // Headline tiles summarise the whole Instance/Market scope (not the current search), fetched once per scope change so they don't slow down search/paging.
  const [statsList, setStatsList] = useState<MarketOrderType[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!selectedInstance) {
      setStatsList([]);
      return;
    }

    setIsStatsLoading(true);
    client.getMarketOrderTypes({
      companyId: selectedInstance === 'all' ? undefined : selectedInstance,
      customerTypeId: selectedMarket || undefined,
      pageNumber: 0,
      pageSize: 2147483647,
      sort: '',
      direction: 'asc',
    })
      .then((result) => {
        if (result.success && result.data) {
          setStatsList(result.data.items ?? []);
        }
      })
      .catch((error) => console.error('Error fetching order channel stats:', error))
      .finally(() => setIsStatsLoading(false));
  }, [selectedInstance, selectedMarket]);

  const dsdChannels = useMemo(() => (
    statsList.filter((orderType) => isOrderTypeDSD(orderType.orderTypeId))
  ), [statsList]);

  const missingCutoffCount = useMemo(() => (
    statsList.filter((orderType) => !orderType.cutoff?.orderCutOffTime).length
  ), [statsList]);

  const dsdMissingSupplierCount = useMemo(() => (
    dsdChannels.filter((orderType) => !orderType.cutoff?.supplierName && !orderType.cutoff?.supplierEmailAddress).length
  ), [dsdChannels]);

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialSearchTerm]);

  const fetchOrderTypes = async () => {
    setIsLoading(true);
    try {
      const options: MarketOrderTypeFilterOptions = {
        companyId: selectedInstance || undefined,
        customerTypeId: selectedMarket || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getMarketOrderTypes(options);
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching order types:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (isMarketsLoading(selectedInstance)) return;

    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId != null ? markets[0].customerTypeId.toString() : '';
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
        return;
      }
    }

    if (selectedInstance || selectedInstance === 'all')
    {
      fetchOrderTypes();
    }

  }, [markets, currentPage, searchTerm, selectedInstance, selectedMarket]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(event.target.value, '');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, event.target.value);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('all');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('all', '', '');
  };

  const handleRowClick = (orderType: MarketOrderType) => {
    if (!canEdit) return;
    const params = new URLSearchParams();
    ['companyId', 'customerTypeId', 'searchTerm'].forEach((key) => {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    });
    navigate(`/procurement/order-channels/edit/${orderType.companyOrderTypeId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const columns: TableColumn<MarketOrderType>[] = [
    { id: 'orderTypeId', label: t('procurementOrderChannelId', 'Order Channel ID'), field: 'orderTypeId' },
    {
      id: 'name',
      label: t('procurementOrderChannelName', 'Order Channel Name'),
      render: (row) => (
        <Tooltip title={row.name}
          placement="top">
          <span>{truncateText(row.name)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'marketName',
      label: t('procurementMarketName', 'Market Name'),
      render: (row) => (
        <Tooltip title={row.marketName || 'All Markets'}
          placement="top">
          <span>{truncateText(row.marketName || 'All Markets')}</span>
        </Tooltip>
      ),
    },
    {
      id: 'instanceName',
      label: t('procurementInstanceName', 'Instance Name'),
      render: (row) => (
        <Tooltip title={row.instanceName}
          placement="top">
          <span>{truncateText(row.instanceName)}</span>
        </Tooltip>
      ),
    },
    {
      id: 'cutoffTime',
      label: t('procurementOrderChannelCutoffTime', 'Order Channel Cutoff Time'),
      align: 'center',
      render: (row) => formatCutoffTimeForDisplay(row.cutoff?.orderCutOffTime),
    },
  ];

  const table: TableSectionProps<MarketOrderType> = {
    title: t('procurementOrderChannels', 'Order Channels'),
    columns,
    rows: list,
    getRowId: (row) => row.companyOrderTypeId,
    onRowClick: canEdit ? handleRowClick : undefined,
    recordCount: totalCount,
    recordCountLabel: t('procurementOrderChannels', 'Order Channels'),
    loading: isLoading,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('procurementOrderChannels', 'Order Channels')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('procurementCreateNew', 'Create New Order Channel')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    onPageChange: handlePageChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('procurementSearchPlaceholder', 'Search order channels...')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
            renderValue={(value) => {
              if (value === '') {
                return <span>{t('allInstances')}</span>;
              }

              return instances?.find(i => i.id != null && i.id.toString() === value)?.name || '';
            }}
          >
            <MenuItem value="all">
              {t('allInstances')}
            </MenuItem>
            {instances
              .filter((instance) => instance.id != null)
              .map((instance) => (
                <MenuItem
                  key={instance.id}
                  value={String(instance.id)}
                >
                  {instance.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByMarket')}
          </InputLabel>
          <Select
            value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
            onChange={handleMarketChange}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value="">
              {t('allMarkets')}
            </MenuItem>
            {markets
              .filter((market) => market.customerTypeId != null)
              .map((market) => (
                <MenuItem
                  key={market.customerTypeId}
                  value={String(market.customerTypeId)}
                >
                  {market.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key="totalOrderChannels"
        label={t('procurementTileTotalOrderChannels', 'Total Order Channels')}
        value={statsList.length}
        description={t('procurementTileTotalOrderChannelsDescription', 'All order channels in the selected Instance/Market')}
        icon={<Route />}
        variant="primary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="dsdChannels"
        label={t('procurementTileDsdChannels', 'DSD Channels')}
        value={dsdChannels.length}
        description={t('procurementTileDsdChannelsDescription', 'Direct Store Delivery channels, out of the total above')}
        icon={<LocalShipping />}
        variant="secondary"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="missingCutoff"
        label={t('procurementTileMissingCutoff', 'Missing Cutoff Time')}
        value={missingCutoffCount}
        description={t('procurementTileMissingCutoffDescription', 'Channels with no cutoff time')}
        icon={<EventBusy />}
        variant="warning"
        loading={isStatsLoading}
      />,
      <InsightTile
        key="dsdMissingSupplier"
        label={t('procurementTileDsdMissingSupplier', 'DSD Missing Supplier Details')}
        value={dsdMissingSupplierCount}
        description={t('procurementTileDsdMissingSupplierDescription', 'DSD channels with no supplier contact set')}
        icon={<ReportProblem />}
        variant="error"
        loading={isStatsLoading}
      />,
    ],
  };

  // Still resolving the default instance/market before the first fetch even starts - keep showing the skeleton, not a zero-data flash.
  return <>{children({ filterBar, headlineContent, table, isInitialLoading: (defaultFiltersLoading || isLoading) && !hasLoadedOnce })}</>;
};

export default OrderChannelsListPageContent;

